import * as pdfjsNamespace from 'pdfjs-dist/build/pdf';

/** pdfjs-dist may expose the API on the module or on `.default` depending on bundler interop. */
const pdfjsLib = /** @type {typeof import('pdfjs-dist/build/pdf')} */ (
  pdfjsNamespace.default ?? pdfjsNamespace
);

if (pdfjsLib?.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function pageBreakHtml() {
  return '<div class="mce-pagebreak pdf-manual-page-break" data-mce-type="pagebreak" contenteditable="false" style="height:0;margin:0;padding:0;overflow:hidden"></div>';
}

/**
 * @param {Array<{ str?: string, transform?: number[], width?: number, fontName?: string, height?: number }>} items
 * @returns {Array<{ str: string, x: number, y: number, w: number, midX: number, fontName: string, fontSize: number }>}
 */
function normalizeItems(items) {
  /** @type {Array<{ str: string, x: number, y: number, w: number, midX: number, fontName: string, fontSize: number }>} */
  const out = [];
  for (const item of items || []) {
    const str = String(item?.str || '');
    const transform = item?.transform;
    if (!str.trim() || !Array.isArray(transform) || transform.length < 6) continue;
    const x = Number(transform[4]) || 0;
    const y = Number(transform[5]) || 0;
    const w = Number(item.width) || 0;
    const scaleY = Math.abs(Number(transform[3]) || 0);
    const scaleX = Math.abs(Number(transform[0]) || 0);
    const fontSize = Math.max(scaleY, scaleX, Number(item.height) || 0) || 11;
    out.push({
      str,
      x,
      y,
      w,
      midX: x + w / 2,
      fontName: String(item.fontName || '').replace(/^[A-Z]{6}\+/, ''),
      fontSize: Math.round(fontSize * 10) / 10,
    });
  }
  return out;
}

/**
 * @param {Array<{ midX: number, w: number, x: number }>} items
 * @param {number} pageWidth
 * @returns {number | null}
 */
function detectColumnSplit(items, pageWidth) {
  if (items.length < 8 || pageWidth < 100) return null;

  const minX = Math.min(...items.map((i) => i.x));
  const maxX = Math.max(...items.map((i) => i.x + i.w));
  const span = maxX - minX;
  if (span < pageWidth * 0.45) return null;

  const bucketSize = Math.max(8, span / 40);
  /** @type {Map<number, number>} */
  const coverage = new Map();
  for (const item of items) {
    const start = Math.floor((item.x - minX) / bucketSize);
    const end = Math.floor((item.x + item.w - minX) / bucketSize);
    for (let b = start; b <= end; b += 1) {
      coverage.set(b, (coverage.get(b) || 0) + 1);
    }
  }

  const midStart = Math.floor(coverage.size * 0.25);
  const midEnd = Math.ceil(coverage.size * 0.75);
  let bestBucket = -1;
  let bestCount = Infinity;
  for (let b = midStart; b <= midEnd; b += 1) {
    const count = coverage.get(b) || 0;
    if (count < bestCount) {
      bestCount = count;
      bestBucket = b;
    }
  }

  const values = [...coverage.values()];
  const avg = values.reduce((a, b) => a + b, 0) / Math.max(1, values.length);
  if (bestBucket < 0 || bestCount > avg * 0.35) return null;

  const split = minX + (bestBucket + 0.5) * bucketSize;
  const leftCount = items.filter((i) => i.midX < split).length;
  const rightCount = items.filter((i) => i.midX >= split).length;
  if (leftCount < 3 || rightCount < 3) return null;
  return split;
}

/**
 * @param {Array<{ str: string, x: number, y: number, w: number }>} items
 * @returns {string[]}
 */
function itemsToLines(items) {
  /** @type {Array<{ y: number, parts: Array<{ x: number, str: string, width: number }> }>} */
  const rows = [];

  for (const item of items) {
    const row = rows.find((r) => Math.abs(r.y - item.y) < 3.5);
    if (row) row.parts.push({ x: item.x, str: item.str, width: item.w });
    else rows.push({ y: item.y, parts: [{ x: item.x, str: item.str, width: item.w }] });
  }

  rows.sort((a, b) => b.y - a.y);

  return rows
    .map((row) => {
      row.parts.sort((a, b) => a.x - b.x);
      let line = '';
      let lastEnd = null;
      for (const part of row.parts) {
        if (lastEnd !== null) {
          const gap = part.x - lastEnd;
          if (gap > 28) line += ' | ';
          else if (gap > 1.2) line += ' ';
        }
        line += part.str;
        lastEnd = part.x + part.width;
      }
      return line.replace(/\s+/g, ' ').trim();
    })
    .filter(Boolean);
}

/**
 * @param {Array<{ str?: string, transform?: number[], width?: number, fontName?: string, height?: number }>} rawItems
 * @param {number} pageWidth
 * @returns {{ lines: string[], multiColumn: boolean, richText: string }}
 */
function itemsToReadingLines(rawItems, pageWidth) {
  const items = normalizeItems(rawItems);
  if (!items.length) return { lines: [], multiColumn: false, richText: '' };

  const split = detectColumnSplit(items, pageWidth);
  const ordered = split == null
    ? items
    : [...items.filter((i) => i.midX < split), ...items.filter((i) => i.midX >= split)];
  const lines = split == null
    ? itemsToLines(items)
    : [...itemsToLines(items.filter((i) => i.midX < split)), ...itemsToLines(items.filter((i) => i.midX >= split))];

  /** @type {Array<{ y: number, parts: typeof ordered }>} */
  const rows = [];
  for (const item of ordered) {
    const row = rows.find((r) => Math.abs(r.y - item.y) < 3.5);
    if (row) row.parts.push(item);
    else rows.push({ y: item.y, parts: [item] });
  }
  rows.sort((a, b) => b.y - a.y);

  const richLines = rows.map((row) => {
    row.parts.sort((a, b) => a.x - b.x);
    return row.parts
      .map((p) => {
        const font = p.fontName ? ` font="${p.fontName}"` : '';
        return `[size=${p.fontSize}${font} x=${Math.round(p.x)} y=${Math.round(p.y)}]${p.str}`;
      })
      .join(' ');
  });

  return {
    lines,
    multiColumn: split != null,
    richText: richLines.join('\n'),
  };
}

/**
 * @param {string[]} lines
 * @returns {string[]}
 */
function linesToParagraphs(lines) {
  /** @type {string[]} */
  const paragraphs = [];
  let buf = '';

  for (const line of lines) {
    if (!line) {
      if (buf) {
        paragraphs.push(buf);
        buf = '';
      }
      continue;
    }
    if (!buf) {
      buf = line;
      continue;
    }
    const prevEndsSentence = /[.!?:…»)]$/.test(buf);
    if (!prevEndsSentence && /^[a-zа-яёàâäéèêëïîôùûüç]/.test(line)) {
      buf = `${buf} ${line}`;
    } else {
      paragraphs.push(buf);
      buf = line;
    }
  }
  if (buf) paragraphs.push(buf);
  return paragraphs;
}

/**
 * @param {string[]} paragraphs
 */
function paragraphsToHtml(paragraphs) {
  return paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join('\n');
}

/**
 * Compact JPEG preview for OpenAI vision (bounded size).
 * @param {import('pdfjs-dist').PDFPageProxy} page
 * @returns {Promise<{ dataUrl: string|null, canvas: HTMLCanvasElement|null, scale: number }>}
 */
async function renderPagePreview(page) {
  const base = page.getViewport({ scale: 1 });
  const targetW = 720;
  const scale = Math.min(1.1, targetW / base.width);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) return { dataUrl: null, canvas: null, scale };
  await page.render({ canvasContext: ctx, viewport }).promise;
  // Keep under Laravel validation (~1.2MB string) for multi-page uploads.
  let quality = 0.62;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);
  while (dataUrl.length > 850000 && quality > 0.35) {
    quality -= 0.08;
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }
  return {
    dataUrl: dataUrl.length > 900000 ? null : dataUrl,
    canvas,
    scale,
  };
}

/**
 * @param {number[]} a
 * @param {number[]} b
 */
function multiplyMatrix(a, b) {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5],
  ];
}

/**
 * Many logos are vector Form XObjects (not bitmaps). Crop them from the rendered page.
 * @param {import('pdfjs-dist').PDFPageProxy} page
 * @param {number} pageNumber
 * @param {HTMLCanvasElement} canvas
 * @param {number} scale
 */
async function extractFormXObjectCrops(page, pageNumber, canvas, scale) {
  /** @type {Array<{ id: string, data_url: string, width: number, height: number }>} */
  const out = [];
  const ops = pdfjsLib?.OPS;
  if (!ops || typeof page.getOperatorList !== 'function') return out;

  try {
    const base = page.getViewport({ scale: 1 });
    const pageH = base.height;
    const pageW = base.width;
    const operatorList = await page.getOperatorList();
    const { fnArray, argsArray } = operatorList;
    /** @type {number[][]} */
    const stack = [];
    let ctm = [1, 0, 0, 1, 0, 0];
    let imgIdx = 0;

    for (let i = 0; i < fnArray.length; i += 1) {
      const fn = fnArray[i];
      if (fn === ops.save) {
        stack.push(ctm.slice());
        continue;
      }
      if (fn === ops.restore) {
        ctm = stack.pop() || [1, 0, 0, 1, 0, 0];
        continue;
      }
      if (fn === ops.transform && Array.isArray(argsArray[i])) {
        ctm = multiplyMatrix(ctm, argsArray[i].map(Number));
        continue;
      }
      if (fn !== ops.paintFormXObjectBegin) continue;

      const args = argsArray[i];
      /** @type {number[] | null} */
      let bbox = null;
      if (Array.isArray(args)) {
        if (Array.isArray(args[1]) && args[1].length >= 4) bbox = args[1].map(Number);
        else if (Array.isArray(args[0]) && args[0].length >= 4 && args[0].length !== 6) bbox = args[0].map(Number);
      }
      if (!bbox) continue;

      const formW = Math.abs(bbox[2] - (bbox[0] || 0)) || Math.abs(bbox[2]);
      const formH = Math.abs(bbox[3] - (bbox[1] || 0)) || Math.abs(bbox[3]);
      if (formW < 24 || formH < 12) continue;
      // Skip near-full-page forms.
      if (formW > pageW * 0.85 || formH > pageH * 0.4) continue;

      const x = ctm[4];
      const y = ctm[5];
      const absScaleX = Math.hypot(ctm[0], ctm[1]) || 1;
      const absScaleY = Math.hypot(ctm[2], ctm[3]) || 1;
      const drawW = formW * absScaleX;
      const drawH = formH * absScaleY;
      if (drawW < 24 || drawH < 12) continue;
      if (drawW > pageW * 0.85 || drawH > pageH * 0.4) continue;

      const topPdf = pageH - (y + drawH);
      const left = Math.max(0, Math.floor(x * scale) - 2);
      const top = Math.max(0, Math.floor(topPdf * scale) - 2);
      const width = Math.min(canvas.width - left, Math.ceil(drawW * scale) + 4);
      const height = Math.min(canvas.height - top, Math.ceil(drawH * scale) + 4);
      if (width < 16 || height < 12) continue;

      const crop = document.createElement('canvas');
      crop.width = width;
      crop.height = height;
      const ctx = crop.getContext('2d');
      if (!ctx) continue;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(canvas, left, top, width, height, 0, 0, width, height);
      const dataUrl = crop.toDataURL('image/png');
      if (!dataUrl || dataUrl.length > 850000) continue;

      out.push({
        id: `p${pageNumber}-logo${imgIdx}`,
        data_url: dataUrl,
        width,
        height,
      });
      imgIdx += 1;
      if (out.length >= 3) break;
    }
  } catch {
    // ignore
  }

  return out;
}

/**
 * @param {unknown} objs
 * @param {string} objectId
 * @returns {Promise<any>}
 */
function getPdfObjAsync(objs, objectId) {
  return new Promise((resolve) => {
    let settled = false;
    const done = (val) => {
      if (settled) return;
      settled = true;
      resolve(val ?? null);
    };
    try {
      const existing = objs?.get?.(objectId, (val) => done(val));
      if (existing !== undefined && existing !== null) done(existing);
      else setTimeout(() => done(null), 1500);
    } catch {
      done(null);
    }
  });
}

/**
 * @param {any} pdfImage
 * @returns {string | null}
 */
function pdfImageToDataUrl(pdfImage) {
  if (!pdfImage || typeof pdfImage !== 'object') return null;
  const width = Number(pdfImage.width) || 0;
  const height = Number(pdfImage.height) || 0;
  if (width < 24 || height < 24) return null;
  // Skip huge full-page raster tiles (those are better left as page preview refs).
  if (width * height > 12_000_000) return null;

  try {
    if (typeof pdfImage.data === 'string' && pdfImage.data.startsWith('data:image/')) {
      return pdfImage.data;
    }
    // JPEG byte stream sometimes exposed directly
    if (pdfImage.kind === 'JPEGStream' && pdfImage.data instanceof Uint8Array) {
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < pdfImage.data.length; i += chunk) {
        binary += String.fromCharCode(...pdfImage.data.subarray(i, i + chunk));
      }
      return `data:image/jpeg;base64,${btoa(binary)}`;
    }

    const data = pdfImage.data;
    if (!(data instanceof Uint8ClampedArray || data instanceof Uint8Array)) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const imageData = ctx.createImageData(width, height);
    if (data.length === width * height * 4) {
      imageData.data.set(data);
    } else if (data.length === width * height * 3) {
      for (let i = 0, j = 0; i < data.length; i += 3, j += 4) {
        imageData.data[j] = data[i];
        imageData.data[j + 1] = data[i + 1];
        imageData.data[j + 2] = data[i + 2];
        imageData.data[j + 3] = 255;
      }
    } else if (data.length === width * height) {
      for (let i = 0, j = 0; i < data.length; i += 1, j += 4) {
        const v = data[i];
        imageData.data[j] = v;
        imageData.data[j + 1] = v;
        imageData.data[j + 2] = v;
        imageData.data[j + 3] = 255;
      }
    } else {
      return null;
    }
    ctx.putImageData(imageData, 0, 0);
    let quality = 0.85;
    let out = canvas.toDataURL('image/png');
    if (out.length > 700000) {
      out = canvas.toDataURL('image/jpeg', quality);
      while (out.length > 700000 && quality > 0.4) {
        quality -= 0.1;
        out = canvas.toDataURL('image/jpeg', quality);
      }
    }
    return out.length > 850000 ? null : out;
  } catch {
    return null;
  }
}

/**
 * Extract embedded bitmaps from a PDF page (logos, photos — not full-page screenshots).
 * @param {import('pdfjs-dist').PDFPageProxy} page
 * @param {number} pageNumber
 * @returns {Promise<Array<{ id: string, data_url: string, width: number, height: number }>>}
 */
async function extractEmbeddedImages(page, pageNumber) {
  /** @type {Array<{ id: string, data_url: string, width: number, height: number }>} */
  const out = [];
  const seen = new Set();
  const ops = pdfjsLib?.OPS;
  if (!ops || typeof page.getOperatorList !== 'function') return out;

  try {
    const operatorList = await page.getOperatorList();
    const { fnArray, argsArray } = operatorList;
    let imgIdx = 0;
    for (let i = 0; i < fnArray.length; i += 1) {
      const fn = fnArray[i];
      const isPaint =
        fn === ops.paintImageXObject
        || fn === ops.paintJpegXObject
        || fn === ops.paintInlineImageXObject
        || fn === ops.paintImageXObjectRepeat;
      if (!isPaint) continue;

      const args = argsArray[i];
      /** @type {any} */
      let pdfImage = null;
      if (fn === ops.paintInlineImageXObject) {
        pdfImage = Array.isArray(args) ? args[0] : null;
      } else {
        const objectId = Array.isArray(args) ? args[0] : null;
        if (!objectId || typeof objectId !== 'string' || seen.has(objectId)) continue;
        seen.add(objectId);
        const store = objectId.startsWith('g_') ? page.commonObjs : page.objs;
        pdfImage = await getPdfObjAsync(store, objectId);
      }

      const dataUrl = pdfImageToDataUrl(pdfImage);
      if (!dataUrl) continue;
      const width = Number(pdfImage?.width) || 0;
      const height = Number(pdfImage?.height) || 0;
      // Prefer logos / photos over tiny icons.
      if (width < 40 || height < 40) continue;
      if (width < 120 && height < 120 && width * height < 8000) continue;

      const id = `p${pageNumber}-${imgIdx}`;
      imgIdx += 1;
      out.push({ id, data_url: dataUrl, width, height });
      if (out.length >= 4) break;
    }
  } catch {
    // ignore extraction failures — text/HTML import still works
  }

  // Prefer larger assets (logos) first.
  out.sort((a, b) => b.width * b.height - a.width * a.height);
  return out.slice(0, 3);
}

/**
 * Extract per-page text + previews + embedded images for server/OpenAI import.
 *
 * @param {Blob|ArrayBuffer} source
 * @param {{ maxPages?: number }} [options]
 * @returns {Promise<{
 *   pages: Array<{ text: string, richText: string, image: string|null, images: Array<{ id: string, data_url: string, width: number, height: number }> }>,
 *   assets: Array<{ id: string, data_url: string, width: number, height: number }>,
 *   pageCount: number,
 *   textLength: number,
 *   multiColumn: boolean,
 *   quality: 'good'|'weak'|'none',
 *   localHtml: string,
 * }>}
 */
export async function preparePdfImport(source, options = {}) {
  if (!pdfjsLib || typeof pdfjsLib.getDocument !== 'function') {
    throw new Error('PDF engine failed to load (pdf.js getDocument is unavailable).');
  }

  const maxPages = Math.max(1, Math.min(20, Number(options.maxPages) || 15));
  const data = source instanceof ArrayBuffer ? source : await source.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const pageCount = pdf.numPages;
  const limit = Math.min(pageCount, maxPages);

  /** @type {Array<{ text: string, richText: string, image: string|null, images: Array<{ id: string, data_url: string, width: number, height: number }> }>} */
  const pages = [];
  /** @type {Array<{ id: string, data_url: string, width: number, height: number }>} */
  const assets = [];
  let textLength = 0;
  let multiColumn = false;
  /** @type {string[]} */
  const htmlParts = [];

  for (let i = 1; i <= limit; i += 1) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    const reading = itemsToReadingLines(content.items, viewport.width);
    if (reading.multiColumn) multiColumn = true;
    const paragraphs = linesToParagraphs(reading.lines);
    const text = paragraphs.join('\n\n').trim();
    textLength += text.length;

    let image = null;
    /** @type {HTMLCanvasElement | null} */
    let previewCanvas = null;
    let previewScale = 1;
    try {
      const preview = await renderPagePreview(page);
      image = preview.dataUrl;
      previewCanvas = preview.canvas;
      previewScale = preview.scale;
    } catch {
      image = null;
    }

    let images = [];
    try {
      images = await extractEmbeddedImages(page, i);
    } catch {
      images = [];
    }
    if (previewCanvas) {
      try {
        const logos = await extractFormXObjectCrops(page, i, previewCanvas, previewScale);
        for (const logo of logos) {
          if (!images.some((img) => Math.abs(img.width - logo.width) < 4 && Math.abs(img.height - logo.height) < 4)) {
            images.unshift(logo);
          }
        }
      } catch {
        // ignore logo crop failures
      }
    }
    // Hard fallback: if page 1 still has no assets, crop a top-left letterhead band.
    if (i === 1 && images.length === 0 && previewCanvas) {
      try {
        const bandH = Math.min(previewCanvas.height, Math.round(previewCanvas.height * 0.12));
        const bandW = Math.min(previewCanvas.width, Math.round(previewCanvas.width * 0.45));
        const crop = document.createElement('canvas');
        crop.width = bandW;
        crop.height = bandH;
        const ctx = crop.getContext('2d');
        if (ctx) {
          ctx.drawImage(previewCanvas, 0, 0, bandW, bandH, 0, 0, bandW, bandH);
          images.push({
            id: `p${i}-header`,
            data_url: crop.toDataURL('image/png'),
            width: bandW,
            height: bandH,
          });
        }
      } catch {
        // ignore
      }
    }
    for (const asset of images) assets.push(asset);

    pages.push({ text, richText: reading.richText || '', image, images });

    if (i > 1) htmlParts.push(pageBreakHtml());
    if (images.length) {
      htmlParts.push(
        images
          .slice(0, 2)
          .map(
            (img) =>
              `<p class="pdf-import-asset"><img data-pdf-asset="${escapeHtml(img.id)}" alt="Image" style="max-width:100%;height:auto" /></p>`,
          )
          .join('\n'),
      );
    }
    if (paragraphs.length) htmlParts.push(paragraphsToHtml(paragraphs));
  }

  let quality = 'none';
  if (textLength >= 400 && !multiColumn) quality = 'good';
  else if (textLength >= 80) quality = 'weak';
  else quality = 'none';

  return {
    pages,
    assets,
    pageCount,
    textLength,
    multiColumn,
    quality,
    localHtml: htmlParts.join('\n') || '<p></p>',
  };
}

/**
 * Local-only PDF→HTML (no OpenAI). Prefer {@link preparePdfImport} + server import for uploads.
 *
 * @param {Blob|ArrayBuffer} source
 * @param {{ title?: string, maxPages?: number, margins?: object }} [options]
 */
export async function pdfBlobToHtml(source, options = {}) {
  const prepared = await preparePdfImport(source, options);
  const title = String(options.title || '').trim() || 'Document';
  if (prepared.quality === 'none') {
    return {
      html: [
        `<h1>${escapeHtml(title)}</h1>`,
        '<p><em>No selectable text was found in this PDF. OpenAI can rebuild an editable version if configured.</em></p>',
      ].join('\n'),
      hasText: false,
      pageCount: prepared.pageCount,
      textLength: 0,
      pagesWithText: 0,
      quality: prepared.quality,
      pages: prepared.pages,
    };
  }

  return {
    html: prepared.localHtml,
    hasText: prepared.textLength >= 8,
    pageCount: prepared.pageCount,
    textLength: prepared.textLength,
    pagesWithText: prepared.pages.filter((p) => p.text.length >= 8).length,
    quality: prepared.quality,
    pages: prepared.pages,
    multiColumn: prepared.multiColumn,
  };
}

/**
 * Import runs only once when needs_text_import is still true (first upload).
 * Never re-import based on layout version bumps — that destroys the saved document.
 *
 * @param {Record<string, unknown>|null|undefined} state
 */
export function editorStateNeedsPdfImport(state) {
  if (!state || typeof state !== 'object') return true;
  if (state.needs_text_import === true) return true;
  if (typeof state.html === 'string' && state.html.trim()) return false;
  if (state.report && typeof state.report === 'object') return false;
  // Uploaded PDF with no HTML yet (interrupted first import).
  if (state.source === 'upload' || state.imported_from_pdf === true) return true;
  return true;
}

/** Stored on successful first import for diagnostics only — never triggers re-import. */
export const PDF_IMPORT_LAYOUT_VERSION = 6;
